import { _ } from "../../../core/polyglot/polyglot.js";
import {
	gripVerticalSolidIcon,
	timesIcon,
} from "../../../core/elements/icons/index.js";
import { groupBy } from "@foretagsplatsen/core-helpers/array";
import {
	makeActualsSubColumnSpec,
	makeBudgetSubColumnSpec,
	makeForecastEstimateSubColumnSpec,
} from "../document/reportItemBuilders.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../../core/list/streams/ArrayStream.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import CardWidget from "../../../core/widgets/CardWidget/CardWidget.js";
import CheckboxWidget from "../../../core/widgets/CheckboxWidget/CheckboxWidget.js";
import deviationTypeFactory from "../../../models/deviationTypes/deviationTypeFactory.js";
import iconAndText from "../../../core/elements/iconAndText/iconAndText.js";
import ListSource from "../../../core/list/ListSource.js";
import makeCardListWidget from "../../../core/widgets/list/CardListWidget/makeCardListWidget.js";
import select from "../../../core/elements/select/select.js";

/**
 * Widget responsible for rendering cards of a report subcolumns to
 * modify them.
 *
 * @param {SubColumnSpec[]} subColumnSpecs - List of subcolumns to be
 *   rendered and modified
 * @param {Forecast[]} forecastEstimates - List of forecast estimates
 *   that can be inserted as a new SubColumnSpec
 * @param {Budget[]} budgets - List of budgets that can be inserted as
 *   a new SubColumnSpec
 * @param {boolean} [canEditActuals] - Whether actuals can be added,
 *   moved or removed
 * @param {function} onChange - Callback taking the list of
 *   subColumnSpec as argument. Called when any change is performed to
 *   the spec list
 */
export default class SubColumnSpecListWidget extends Widget {
	constructor({
		subColumnSpecs = [],
		forecastEstimates = [],
		budgets = [],
		canEditActuals = false,
		onChange = (_specs) => {},
	}) {
		super(...arguments);

		this._onChange = (specs) => {
			onChange(specs);
			this.update();
		};

		this._forecastEstimates = forecastEstimates;
		this._budgets = budgets;
		this._canEditActuals = canEditActuals;

		this._specs = [...subColumnSpecs];
	}

	renderContentOn(html) {
		html.div(
			{ class: "SubColumnSpecListWidget" },
			this._columnSelectRenderer(),
			this._cardListRenderer(),
		);
	}

	_columnSelectRenderer() {
		return select({
			items: this._makeColumnItems(),
			allowEmptySelection: true,
			emptyLabel: _("Add a column"),
			selectedItem: null,
			onChange: this._addColumn.bind(this),
			dataTest: "select-subcolumn-type",
		});
	}

	_makeColumnItems() {
		const items = [];

		if (this._forecastEstimates.length)
			items.push(this._makeForecastColumnItem());

		if (this._budgets.length) items.push(this._makeBudgetColumnItem());
		if (this._canEditActuals) items.push(this._makeActualsColumnItem());

		return items;
	}

	_makeForecastColumnItem() {
		const label = _("Forecast Estimate");

		return {
			label,
			value: {
				label,
				items: this._makeForecastItems(),
				addItem: this._addForecastEstimate.bind(this),
			},
		};
	}

	_makeForecastItems() {
		return this._forecastEstimates
			.map((forecastEstimate) => ({
				label: forecastEstimate.getName(),
				value: forecastEstimate,
			}))
			.sort(this._sortItems.bind(this));
	}

	_makeBudgetColumnItem() {
		const label = _("Budgets");

		return {
			label,
			value: {
				label,
				items: this._makeBudgetItems(),
				addItem: this._addBudget.bind(this),
			},
		};
	}

	_makeBudgetItems() {
		const groups = groupBy({
			array: this._budgets,
			getKey: (budget) => budget.getFiscalYear().toSmartString(),
		});

		return groups
			.map((group) => ({
				label: group.key,
				options: group.value.map((budget) => ({
					label: budget.getName(),
					value: budget,
				})),
			}))
			.sort(this._sortItems.bind(this));
	}

	_makeActualsColumnItem() {
		return {
			label: _("Actuals"),
			value: { addItem: this._addActuals.bind(this) },
		};
	}

	_sortItems(one, another) {
		return another.label.localeCompare(one.label);
	}

	_addForecastEstimate(forecastEstimate) {
		this._addSpec(makeForecastEstimateSubColumnSpec(forecastEstimate));
	}

	_addBudget(budget) {
		this._addSpec(makeBudgetSubColumnSpec(budget));
	}

	_addActuals() {
		this._addSpec(makeActualsSubColumnSpec());
	}

	_addColumn({ label, items, addItem }) {
		if (!items) {
			addItem();
			return;
		}

		this._configure({
			title: label,
			subtitle: _("Add {columnType} column", { columnType: label }),
			formSpec: {
				klass: "main-form",
				saveAction: ({ subcolumn }) => addItem(subcolumn),
			},
			formBuilder: (form) =>
				form.selectField({
					attribute: "subcolumn",
					dataTest: "select-subcolumn",
					label: _("Name"),
					items,
				}),
		});
	}

	_cardListRenderer() {
		return makeCardListWidget({
			source: ListSource.makeAndFetch({
				stream: new ArrayStream({ data: this._specs }),
			}),
			cssClass: "columns-cards",
			dragAndDrop: {
				isDraggable: ({ item }) => this._canEditSpec(item),
				isDroppable: ({ item }) => this._canEditSpec(item),
				onManualSort: ({ items }) =>
					this._specs.setSubColumnSpecs(items),
				getDragHandle: (item) => item.querySelector(".drag-handle"),
			},
			itemRenderer: this._makeSpecCard.bind(this),
		});
	}

	_canEditSpec(spec) {
		return !spec.isActualsSpec() || this._canEditActuals;
	}

	_makeSpecCard(spec, index) {
		return new CardWidget({
			title: this._cardTitleRenderer({ spec }),
			subtitle: this._cardSubtitleRenderer({ spec }),
			cssClass: "column-card",
			footer: this._cardFooterRenderer({ spec, index }),
		});
	}

	_cardSubtitleRenderer({ spec }) {
		if (spec.isActualsSpec()) {
			return undefined;
		}

		return (html) => {
			html.div(
				{
					class: "deviation-input",
					"data-test": "deviation-group",
				},
				html.label(_("Deviation:")),
				deviationTypeFactory.makeAll().map(
					(deviationType) =>
						new CheckboxWidget({
							toggleCallback: (checked) => {
								checked
									? spec.addDeviationType(deviationType)
									: spec.removeDeviationType(deviationType);

								this._onChange(this._specs);
							},
							checked: spec.hasDeviationType(deviationType),
							label: deviationType.getLabel(),
						}),
				),
			);
		};
	}

	_cardTitleRenderer({ spec }) {
		const title = (html) =>
			html.span({ "data-test": "card-title" }, spec.getTitle());

		if (!this._canEditSpec(spec)) return title;

		return iconAndText(
			{ icon: gripVerticalSolidIcon({ class: "drag-handle" }) },
			title,
		);
	}

	_cardFooterRenderer({ spec, index }) {
		if (!this._canEditSpec(spec)) return null;

		return (html) =>
			html.div(
				{ class: "actions" },
				button({
					class: "action remove",
					dataTest: "remove-card",
					danger: true,
					link: true,
					icon: timesIcon,
					onClick: () => this._removeSpecByIndex(index),
				}),
			);
	}

	_setSpecs(specs) {
		this._specs = specs;
		this._onChange(specs);
	}

	_addSpec(spec) {
		this._specs.push(spec);
		this._onChange(this._specs);
	}

	_removeSpecByIndex(index) {
		this._specs = this._specs.filter(
			(_spec, specIndex) => specIndex !== index,
		);

		this._onChange(this._specs);
	}
}
